import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import RecordingControls from './components/RecordingControls';
import TranscriptPanel from './components/TranscriptPanel';
import AISummaryPanel from './components/AISummaryPanel';
import NotesEditor from './components/NotesEditor';
import SessionStats from './components/SessionStats';
import SettingsPanel from './components/SettingsPanel';
import UseCases from './components/UseCases';
import TipsCard from './components/TipsCard';

interface TranscriptEntry {
  id: string;
  timestamp: string;
  text: string;
  speaker?: string;
}

interface SessionData {
  words: number;
  segments: number;
  duration: string;
  topics: string[];
}

const AINoteTaberPage = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState<'transcript' | 'summary' | 'notes' | 'settings'>('transcript');
  const [sessionStats, setSessionStats] = useState<SessionData>({
    words: 0,
    segments: 0,
    duration: '00:00',
    topics: []
  });
  const [settings, setSettings] = useState({
    noiseCancellation: true,
    autoSave: true,
    speakerDetection: false,
    punctuation: true,
    smartFormatting: true
  });
  const [recordingTime, setRecordingTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRecording, isPaused]);

  useEffect(() => {
    const minutes = Math.floor(recordingTime / 60);
    const seconds = recordingTime % 60;
    setSessionStats(prev => ({
      ...prev,
      duration: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }));
  }, [recordingTime]);

  const handleStart = () => {
    setIsRecording(true);
    setIsPaused(false);
    simulateTranscription();
  };

  const handlePause = () => {
    setIsPaused(true);
  };

  const handleResume = () => {
    setIsPaused(false);
    simulateTranscription();
  };

  const handleStop = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const simulateTranscription = () => {
    const demoTexts = [
      "Let's start today's meeting by reviewing the quarterly results.",
      "Our revenue increased by 23% compared to last quarter.",
      "The marketing campaign performed exceptionally well in the European market.",
      "We need to allocate more resources to the product development team.",
      "Action item: Schedule a follow-up meeting with the design team next week.",
      "Customer feedback has been overwhelmingly positive about the new features.",
      "We should consider expanding our customer support hours.",
      "The next milestone is set for the end of Q2.",
      "Budget approval is pending from the finance department.",
      "Let's discuss the roadmap for the upcoming product launch."
    ];

    let index = 0;
    const interval = setInterval(() => {
      if (!isRecording || isPaused || index >= demoTexts.length) {
        clearInterval(interval);
        return;
      }

      const now = new Date();
      const timestamp = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      
      const newEntry: TranscriptEntry = {
        id: `entry-${Date.now()}-${index}`,
        timestamp,
        text: demoTexts[index],
        speaker: settings.speakerDetection ? (index % 3 === 0 ? 'Speaker 1' : index % 3 === 1 ? 'Speaker 2' : 'Speaker 3') : undefined
      };

      setTranscript(prev => [...prev, newEntry]);
      
      const wordCount = demoTexts[index].split(' ').length;
      setSessionStats(prev => ({
        ...prev,
        words: prev.words + wordCount,
        segments: prev.segments + 1,
        topics: [...new Set([...prev.topics, 'Business', 'Strategy', 'Product Development', 'Marketing'])]
      }));

      index++;
    }, 3000);
  };

  const handleClearTranscript = () => {
    setTranscript([]);
    setSessionStats({
      words: 0,
      segments: 0,
      duration: '00:00',
      topics: []
    });
    setRecordingTime(0);
  };

  const handleExport = (format: string) => {
    let content = '';
    
    if (format === 'txt' || format === 'md') {
      content = `AI Note-Taker Session\nDate: ${new Date().toLocaleDateString()}\nDuration: ${sessionStats.duration}\n\n`;
      content += 'TRANSCRIPT:\n\n';
      transcript.forEach(entry => {
        content += `[${entry.timestamp}]${entry.speaker ? ` ${entry.speaker}:` : ''} ${entry.text}\n`;
      });
      
      if (notes) {
        content += '\n\nNOTES:\n\n';
        content += notes;
      }
    }

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `note-taker-${Date.now()}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-500/10 via-blue-500/10 to-purple-500/10"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-teal-400/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-blue-500 text-white rounded-full text-sm font-medium mb-6">
              <i className="ri-sparkling-fill"></i>
              AI-Powered Note Taker
            </div>
            
            <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Record, Transcribe &<br />Summarize with AI
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto leading-relaxed">
              Transform your voice into organized notes instantly. Perfect for meetings, lectures, interviews, and brainstorming sessions.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <i className="ri-mic-fill text-teal-500"></i>
                <span className="text-sm font-medium text-slate-700">Record</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <i className="ri-file-text-fill text-blue-500"></i>
                <span className="text-sm font-medium text-slate-700">Transcribe</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <i className="ri-ai-generate text-purple-500"></i>
                <span className="text-sm font-medium text-slate-700">Summarize</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-slate-200">
                <i className="ri-download-fill text-orange-500"></i>
                <span className="text-sm font-medium text-slate-700">Export</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main App Interface */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Recording & Stats */}
            <div className="lg:col-span-1 space-y-6">
              <RecordingControls
                isRecording={isRecording}
                isPaused={isPaused}
                duration={sessionStats.duration}
                onStart={handleStart}
                onPause={handlePause}
                onResume={handleResume}
                onStop={handleStop}
              />
              
              <SessionStats stats={sessionStats} />
              
              <TipsCard />
            </div>

            {/* Middle Column - Transcript/Summary/Notes/Settings */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
                {/* Tabs */}
                <div className="flex items-center border-b border-slate-200 bg-slate-50">
                  <button
                    onClick={() => setActiveTab('transcript')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'transcript'
                        ? 'text-teal-600 border-b-2 border-teal-600 bg-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <i className="ri-file-text-line mr-2"></i>
                    Transcript
                  </button>
                  <button
                    onClick={() => setActiveTab('summary')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'summary'
                        ? 'text-teal-600 border-b-2 border-teal-600 bg-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <i className="ri-ai-generate mr-2"></i>
                    AI Summary
                  </button>
                  <button
                    onClick={() => setActiveTab('notes')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'notes'
                        ? 'text-teal-600 border-b-2 border-teal-600 bg-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <i className="ri-edit-line mr-2"></i>
                    Notes
                  </button>
                  <button
                    onClick={() => setActiveTab('settings')}
                    className={`flex-1 px-6 py-4 text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === 'settings'
                        ? 'text-teal-600 border-b-2 border-teal-600 bg-white'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <i className="ri-settings-3-line mr-2"></i>
                    Settings
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {activeTab === 'transcript' && (
                    <TranscriptPanel
                      transcript={transcript}
                      isRecording={isRecording}
                      onClear={handleClearTranscript}
                      onExport={handleExport}
                    />
                  )}
                  
                  {activeTab === 'summary' && (
                    <AISummaryPanel
                      transcript={transcript}
                      isRecording={isRecording}
                    />
                  )}
                  
                  {activeTab === 'notes' && (
                    <NotesEditor
                      notes={notes}
                      onChange={setNotes}
                    />
                  )}
                  
                  {activeTab === 'settings' && (
                    <SettingsPanel
                      settings={settings}
                      onChange={setSettings}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <UseCases />

      {/* Inspired By Section */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-8 border border-slate-200">
            <div className="flex items-center justify-center gap-4 flex-wrap">
              <i className="ri-lightbulb-flash-line text-3xl text-teal-500"></i>
              <p className="text-slate-700 text-lg">
                Inspired by{' '}
                <a
                  href="https://apps.apple.com/us/app/wave-ai-note-taker-recorder/id6451491556"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 hover:text-teal-700 font-semibold underline decoration-2 underline-offset-4"
                >
                  Wave AI Note Taker & Recorder
                </a>
                {' '}— Available on the App Store
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AINoteTaberPage;